import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PlusOutlined, FileImageOutlined, LoadingOutlined } from '@ant-design/icons';
import { Form, Input, Upload, Row, Col, Select, Avatar, Tooltip, Spin, Tabs, Modal } from 'antd';
import config from '../config';
import { beforeUpload, getFile } from '../common_functions';
import UploadTrigger from './UploadTrigger';

/* ---------- Small, reusable bits ---------- */

const ImageWithLoader = React.memo(function ImageWithLoader({ imgId, size = 28, alt = 'stamp' }) {
  const [loaded, setLoaded] = useState(false);
  const src = imgId ? getFile(imgId) : null;

  if (!src) {
    return <Avatar icon={<FileImageOutlined />} shape="square" style={{ width: size, height: size }} />;
  }

  return (
    <div style={{ position: 'relative', width: size, height: size, display: 'inline-block' }}>
      {!loaded && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spin indicator={<LoadingOutlined spin />} size="small" />
        </div>
      )}
      <img
        src={src}
        alt={alt}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
        style={{
          width: size,
          height: size,
          objectFit: 'cover',
          borderRadius: 6,
          opacity: loaded ? 1 : 0,
          transition: 'opacity 160ms ease',
          display: 'block',
        }}
      />
    </div>
  );
});

const UploadItems = React.memo(function UploadItems({ uploadFile, handlePreview, name, file_name, fileList }) {
  return (
    <Upload
      name="file"
      listType="picture"
      className="create_object"
      accept={config.FileType.imageType.toString()}
      beforeUpload={file => beforeUpload(file, 'imageType', name.replaceAll('_', '.'))}
      maxCount={1}
      multiple={false}
      onChange={e => uploadFile(e, name, file_name)}
      fileList={fileList}
      onPreview={handlePreview}
      showUploadList={{ showRemoveIcon: false }}
    >
      <UploadTrigger fileType="imageType" />
    </Upload>
  );
});

/* ---------- Main component ---------- */

const GenFieldArray = ({
  permissions = { role: '', tabs_permission: '' },
  fieldName,
  uploadFile,
  handlePreview,
  buttonText = 'Add stamp',
  label = 'Notes',
  extraFields = {},
  fileData = [{}],
  onSearch = null,
  fetchSubStamps = null,
  form = null, // Antd Form instance
  initialValues = null,
}) => {
  // childStamps is per-row (index) so each row's sub-options are isolated
  const [childStamps, setChildStamps] = useState({}); // { [rowIndex]: Array<SubStamp> }
  const [stampCache, setStampCache] = useState({}); // { [parentId]: Array<SubStamp> }
  const [activeKey, setActiveKey] = useState('0');

  const labelBase = useMemo(() => fieldName?.replaceAll('_', ' '), [fieldName]);
  const fileKeyBase = useMemo(() => fieldName?.replaceAll('_', '-'), [fieldName]);

  /** Get sub-stamps for a parentId (cached). Returns the array. */
  const loadSubStamps = useCallback(
    async parentId => {
      if (!parentId || !fetchSubStamps) return [];
      // Return from cache if present
      if (stampCache[parentId]) return stampCache[parentId];
      // Fetch and cache
      const data = (await fetchSubStamps(parentId)) || [];
      setStampCache(prev => ({ ...prev, [parentId]: data }));
      return data;
    },
    [fetchSubStamps, stampCache]
  );

  const handleParentStampChange = useCallback(
    async (parentId, rowIndex) => {
      // clear subgroup on parent change
      if (form && typeof form.setFields === 'function') {
        form.setFields([{ name: [fieldName, rowIndex, 'stamp_subgroup'], value: undefined }]);
      }

      // Load sub-stamps
      const subs = await loadSubStamps(parentId);
      setChildStamps(prev => ({ ...prev, [rowIndex]: subs }));

      // If no sub-stamps → explicitly clear it
      if (!subs.length && form && typeof form.setFieldsValue === 'function') {
        form.setFieldsValue({
          [fieldName]: {
            [rowIndex]: {
              stamp_subgroup: undefined,
            },
          },
        });
      }
    },
    [fieldName, form, loadSubStamps]
  );

  /** Edit mode: pre-populate sub-stamps where a parent stamp already exists */
  useEffect(() => {
    const hydrateFrom = form?.getFieldValue?.(fieldName) || initialValues?.[fieldName] || [];
    if (!hydrateFrom?.length) return;

    (async () => {
      const next = {};
      for (let idx = 0; idx < hydrateFrom.length; idx++) {
        const row = hydrateFrom[idx];
        if (row?.stamp) {
          const subs = await loadSubStamps(row.stamp);
          next[idx] = subs;
        }
      }
      if (Object.keys(next).length) setChildStamps(prev => ({ ...prev, ...next }));
    })();
  }, [fieldName, form, initialValues, loadSubStamps]);

  /** Mutating props is an anti-pattern; kept for compatibility with your current usage. */
  const updateFileData = useCallback(
    (name, files) => {
      if (fileData && Object.prototype.hasOwnProperty.call(fileData, files)) {
        delete fileData[files];
        const updatedData = {};
        let count = 1;
        for (const key in fileData) {
          if (Object.prototype.hasOwnProperty.call(fileData, key)) {
            updatedData[`${name}-${count}`] = fileData[key];
            count++;
          }
        }
        fileData = updatedData;
      }
    },
    [fileData]
  );

  return (
    <Form.List name={fieldName} initialValue={[{}]}>
      {(fields, { add, remove }) => {
        const nextKey = fields.length ? Math.max(...fields.map(f => f.key)) + 1 : 0;

        const confirmRemove = (targetKey, targetName, rowIndex) => {
          Modal.confirm({
            title: 'Delete entry',
            content: 'Are you sure you want to delete this entry?',
            okText: 'Yes',
            okButtonProps: { danger: true },
            cancelText: 'No',
            onOk: () => {
              remove(targetName);
              updateFileData(fileKeyBase, `${fileKeyBase}-${rowIndex + 1}`);
              const idx = fields.findIndex(f => String(f.key) === targetKey);
              const fallback = fields[idx - 1] || fields[idx + 1];
              if (fallback) setActiveKey(String(fallback.key));
            },
          });
        };

        const handleEdit = (targetKey, action) => {
          if (action === 'add') {
            add({});
            setActiveKey(String(nextKey));
          } else {
            const target = fields.find(f => String(f.key) === targetKey);
            if (target) confirmRemove(targetKey, target.name, target.name);
          }
        };

        return (
          <Tabs type="editable-card" className="bp-array-tabs" activeKey={activeKey} onChange={setActiveKey} onEdit={handleEdit} addIcon={<Tooltip title={buttonText}>
                <PlusOutlined />
              </Tooltip>} items={fields.map(({
  key,
  name,
  ...restField
}) => {
  const rowIndex = name; // stable index for this render
  const canDelete = key !== 0 && (permissions.role === 'admin' || !!permissions.tabs_permission);
  return {
    label: `Entry ${rowIndex + 1}`,
    key: String(key),
    closable: canDelete,
    forceRender: true,
    children: <Row gutter={[16, 16]} align="top">
                {/* Upload */}
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item name={[name, 'file']} {...restField} style={{
          textTransform: 'capitalize'
        }} label={`${labelBase} ${rowIndex + 1}`}>
                    <UploadItems uploadFile={uploadFile} handlePreview={handlePreview} name={fieldName} file_name={`${fileKeyBase}-${rowIndex + 1}`} fileList={fileData?.[rowIndex]?.[`${fileKeyBase}-${rowIndex + 1}`]} />
                  </Form.Item>
                </Col>

                {/* Notes */}
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item {...restField} name={[name, 'notes']} label={`${label} ${rowIndex + 1}`}>
                    <Input.TextArea placeholder="Enter notations..." />
                  </Form.Item>
                </Col>

                {/* Example: Annotation Recto Material */}
                {Array.isArray(extraFields?.annotation_recto_material) && <Col xs={24} sm={12} lg={8}>
                    <Form.Item {...restField} name={[name, 'annotation_material_recto']} label={`Annotation Recto ${rowIndex + 1} Material`}>
                      <Select showSearch allowClear optionFilterProp="children" filterOption={(input, option) => option?.children?.toString()?.toLowerCase()?.includes(input.toLowerCase())} placeholder="Select annotation recto material">
                        {extraFields.annotation_recto_material.map(el => <Select.Option key={el.id} value={el.id}>
                            {el.name}
                          </Select.Option>)}
                      </Select>
                    </Form.Item>
                  </Col>}
                {extraFields?.annotation_verso_material ? <Col xs={24} sm={12} lg={8}>
                    <Form.Item {...restField} name={[name, 'annotation_material']} label={`Annotation Verso ${name + 1} Material`}>
                      <Select showSearch allowClear optionFilterProp="children" filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())} placeholder="Select annotation verso material" style={{
            textTransform: 'capitalize'
          }}>
                        {extraFields?.annotation_verso_material.map(el => <Select.Option key={el.id} value={el.id} onMouseEnter={() => {}} onMouseLeave={() => {}}>
                            <Row justify="space-between">
                              <Col>{el.name}</Col>
                            </Row>
                          </Select.Option>)}
                      </Select>
                    </Form.Item>
                  </Col> : ''}

                {extraFields?.annotation_verso_handwriting ? <Col xs={24} sm={12} lg={8}>
                    <Form.Item {...restField} name={[name, 'hand_writing_notes']} label={`Annotation Verso Handwriting ${name + 1}`}>
                      <Select showSearch allowClear virtual={false} optionFilterProp="children" filterOption={false} onSearch={onSearch} placeholder="Select annotation verso handwriting">
                        {extraFields?.annotation_verso_handwriting.map(el => <Select.Option key={el.id} value={el.id}>
                            {el.name}
                          </Select.Option>)}
                      </Select>
                    </Form.Item>
                  </Col> : ''}
                {extraFields?.annotation_recto_handwriting ? <Col xs={24} sm={12} lg={8}>
                    <Form.Item {...restField} name={[name, 'hand_writing_notes']} label={`Annotation Recto Handwriting ${name + 1}`}>
                      <Select showSearch allowClear virtual={false} optionFilterProp="children" filterOption={false} onSearch={onSearch} placeholder="Select annotation recto handwriting">
                        {extraFields?.annotation_recto_handwriting.map(el => <Select.Option key={el.id} value={el.id}>
                            {el.name}
                          </Select.Option>)}
                      </Select>
                    </Form.Item>
                  </Col> : ''}
                {/* Parent Stamp */}
                {Array.isArray(extraFields?.stamp_verso_type) && <Col xs={24} sm={12} lg={8}>
                    <Form.Item {...restField} name={[name, 'stamp']} label={`Stamp Verso Type ${rowIndex + 1}`}>
                      <Select showSearch allowClear optionFilterProp="label" // ✅ search against label
          placeholder="Select stamp verso type" onChange={value => handleParentStampChange(value, rowIndex)}>
                        {extraFields.stamp_verso_type.map(el => <Select.Option key={el.id} value={el.id} label={el.stamp_name} // ✅ searchable text
            >
                            <Row justify="space-between" align="middle" gutter={8}>
                              <Col flex="auto">{el.stamp_name}</Col>
                              <Col>
                                <Tooltip title={<div style={{
                    padding: 4
                  }}>
                                      <ImageWithLoader imgId={el.picture} size={120} alt="preview" />
                                    </div>} destroyTooltipOnHide>
                                  <ImageWithLoader imgId={el.picture} size={24} />
                                </Tooltip>
                              </Col>
                            </Row>
                          </Select.Option>)}
                      </Select>
                    </Form.Item>
                  </Col>}

                {/* Sub-stamps - only for this row */}
                {Array.isArray(childStamps[rowIndex]) && childStamps[rowIndex].length > 0 && <Col xs={24} sm={12} lg={8}>
                    <Form.Item {...restField} name={[name, 'stamp_subgroup']} label={`Stamp Verso Subgroup ${rowIndex + 1}`}>
                      <Select showSearch allowClear optionFilterProp="label" // ✅ search against label
          placeholder="Select stamp verso subgroup">
                        {childStamps[rowIndex].map(el => <Select.Option key={el.id} value={el.id} label={el.stamp_name} // ✅ searchable text
            >
                            <Row justify="space-between" align="middle" gutter={8}>
                              <Col flex="auto">{el.stamp_name}</Col>
                              <Col>
                                <Tooltip title={<div style={{
                    padding: 4
                  }}>
                                      <ImageWithLoader imgId={el.picture} size={120} alt="preview" />
                                    </div>} destroyTooltipOnHide>
                                  <ImageWithLoader imgId={el.picture} size={24} />
                                </Tooltip>
                              </Col>
                            </Row>
                          </Select.Option>)}
                      </Select>
                    </Form.Item>
                  </Col>}

                {extraFields?.stamp_verso_handwriting ? <Col xs={24} sm={12} lg={8}>
                    <Form.Item {...restField} name={[name, 'hand_writing_notes']} label={`Stamp Verso Handwriting ${name + 1}`}>
                      <Select showSearch allowClear virtual={false} optionFilterProp="children" filterOption={false} onSearch={onSearch} placeholder="Select stamp verso handwriting">
                        {extraFields?.stamp_verso_handwriting.map(el => <Select.Option key={el.id} value={el.id}>
                            {el.name}
                          </Select.Option>)}
                      </Select>
                    </Form.Item>
                  </Col> : ''}
                </Row>
  };
})} />
        );
      }}
    </Form.List>
  );
};

export default GenFieldArray;
